<template>
  <ListPage variant="page">
    <div>
      <CreateCustomerModal
        v-model:isOpen="isCreateCustomerOpen"
        @createdCustomer="createdCustomer"
        :showingCustomerType="showingCustomerType"
      />

      <CustomerDetails
        v-model:isOpen="isCustomerDetailsOpen"
        @close="closeCustomerDetails"
        :customerId="currentCustomerId"
      />

      <div class="tw-mb-4 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50 tw-p-4 sm:tw-p-5">
        <div class="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div class="tw-min-w-0">
            <div class="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3 tw-gap-y-1">
              <h1 class="tw-text-2xl sm:tw-text-3xl tw-font-semibold tw-tracking-tight tw-text-slate-800">Customers</h1>
              <div class="tw-text-sm tw-text-slate-600">Manage customers and receivers.</div>
            </div>
          </div>

          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <button v-if="!isSearching" type="button"
              class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-shadow-slate-900/10 hover:tw-bg-slate-800"
              @click="isCreateCustomerOpen = true">
              Add new {{ showingCustomerType == 'billing' ? 'customer' : 'receiver' }}
            </button>
          </div>
        </div>

        <div class="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <button type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="showingCustomerType === 'billing' ? 'tw-border-transparent tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-700 hover:tw-bg-white'"
                @click="setCustomerType('billing')">
                Customers
              </button>
              <button type="button"
                class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                :class="showingCustomerType === 'receiving' ? 'tw-border-transparent tw-bg-slate-900 tw-text-white' : 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-700 hover:tw-bg-white'"
                @click="setCustomerType('receiving')">
                Receivers
              </button>

              <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700 tw-cursor-pointer">
                  <input type="checkbox" v-model="showActiveOnly" @change="toggleActiveFilter"
                    class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-slate-900" />
                  Active only
                </label>

                <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700 tw-cursor-pointer">
                  <input type="checkbox" v-model="showDeactivatedOnly" @change="toggleDeactivatedFilter"
                    class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-slate-900" />
                  Deactivated only
                </label>
              </div>
            </div>
          </div>

          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <div class="tw-text-sm tw-text-slate-600" v-if="!isSearching">
              Total # {{ showingCustomerType }}: <span class="tw-font-semibold tw-text-slate-900">{{ customersCount }}</span>
            </div>
            <div class="tw-w-full sm:tw-w-[320px]">
              <SearchInput 
                v-model="searchTerm" 
                :placeholder="`Search ${showingCustomerType == 'billing' ? 'customer' : 'receiver'} name`" 
                class="tw-w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <ListPage variant="card" :loading="$apollo.queries.customers && $apollo.queries.customers.loading" :cardPadding="false">
        <div class="tw-overflow-auto">
          <table class="tw-w-full tw-border-collapse tw-text-left tw-text-sm tw-text-slate-800">
            <thead>
              <tr class="tw-border-b tw-border-slate-200/70 tw-bg-white/50">
                <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Customer Name</th>
                <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">City</th>
                <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">State</th>
                <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700"># Containers</th>
                <th class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700">Type</th>
                <th v-if="isAdmin" class="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-font-semibold tw-text-slate-700 tw-text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="sortedCustomers.length === 0" class="tw-border-b tw-border-slate-200/50">
                <td class="tw-px-4 tw-py-6 tw-text-sm tw-text-slate-600" :colspan="isAdmin ? 6 : 5">
                  No customers found.
                </td>
              </tr>

              <tr v-for="customer in sortedCustomers" :key="customer.id || customer.name"
                class="tw-border-b tw-border-slate-200/50 hover:tw-bg-slate-50/70 tw-cursor-pointer"
                :class="[!customer.isActive ? 'tw-opacity-60' : '']"
                @click="selectCustomer(customer.id)">
                
                <td class="tw-px-4 tw-py-3 tw-font-medium" :class="customer.isActive ? 'tw-text-slate-900' : 'tw-text-slate-500'">
                  {{ customer.displayName ? customer.displayName : customer.name }}
                  <span v-if="!customer.isActive" class="tw-ml-2 tw-text-[10px] tw-uppercase tw-bg-slate-100 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-slate-500">Inactive</span>
                </td>

                <td class="tw-px-4 tw-py-3 tw-text-slate-700">
                  <span v-if="customer.deliveryAddress">{{ customer.deliveryAddress.city }}</span>
                  <span v-else-if="customer.billingAddress">{{ customer.billingAddress.city }}</span>
                  <span v-else class="tw-text-slate-400">—</span>
                </td>
                <td class="tw-px-4 tw-py-3 tw-text-slate-700">
                  <span v-if="customer.deliveryAddress">{{ customer.deliveryAddress.state }}</span>
                  <span v-else-if="customer.billingAddress">{{ customer.billingAddress.state }}</span>
                  <span v-else class="tw-text-slate-400">—</span>
                </td>
                <td class="tw-px-4 tw-py-3 tw-text-slate-700">
                  {{ customer.activeContainersCount || 0 }}
                </td>
                <td class="tw-px-4 tw-py-3 tw-text-slate-700">{{ customerType(customer) }}</td>

                <td v-if="isAdmin" class="tw-px-4 tw-py-3 tw-text-right">
                  <button v-if="customer.isActive" type="button"
                    class="tw-text-slate-400 hover:tw-text-red-600 tw-transition-colors tw-p-2"
                    title="Deactivate" @click.stop="openStatusModal(customer)">
                    <font-awesome-icon icon="trash" />
                  </button>
                  <button v-else type="button"
                    class="tw-text-slate-400 hover:tw-text-emerald-600 tw-transition-colors tw-p-2"
                    title="Activate" @click.stop="openStatusModal(customer)">
                    <font-awesome-icon icon="rotate-right" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ListPage>

      <div class="tw-mt-4 tw-flex tw-justify-center">
        <PaginationBar :page="page" :totalPages="totalPages" @previousPage="updatePage(-1)" @nextPage="updatePage(1)" @goToPage="goToPage" />
      </div>
    </div>
  </ListPage>

  <Modal v-model:isOpen="isStatusModalOpen" modalId="customer-status-modal" maxWidth="sm" @close="isStatusModalOpen = false">
    <template #title>{{ customerToUpdate?.isActive ? 'Deactivate Customer' : 'Activate Customer' }}</template>
    <template #content>
      <div class="tw-p-1">
        <p class="tw-text-sm tw-text-slate-600">
          Are you sure you want to {{ customerToUpdate?.isActive ? 'deactivate' : 'activate' }}
          <span class="tw-font-bold tw-text-slate-900">{{ customerToUpdate?.name }}</span>?
        </p>

        <div class="tw-mt-6 tw-flex tw-justify-end tw-gap-3">
          <button type="button" class="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700" @click="isStatusModalOpen = false">
            Cancel
          </button>
          <button type="button"
            class="tw-inline-flex tw-items-center tw-rounded-xl tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm"
            :class="customerToUpdate?.isActive ? 'tw-bg-red-600 hover:tw-bg-red-700' : 'tw-bg-emerald-600 hover:tw-bg-emerald-700'"
            @click="confirmToggleActive" :disabled="isProcessing">
            <font-awesome-icon v-if="isProcessing" icon="circle-notch" spin class="tw-mr-2" />
            {{ customerToUpdate?.isActive ? 'Deactivate' : 'Activate' }}
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import SearchInput from '@/components/core/SearchInput.vue';
import ListPage from '@/components/core/ListPage.vue';
import Customers from '../../gql/customers/Customers.gql';
import CreateCustomerModal from './CreateCustomerModal.vue';
import CustomerDetails from './CustomerDetails.vue';
import PaginationBar from '../core/PageElements/PaginationBar.vue';
import { Customer } from '../../types';
import EditCustomerActive from '../../gql/customers/EditCustomerActive.gql';
import UserQuery from '@/gql/user/user.gql';
import Modal from '@/components/core/Modal/Modal.vue';
import { pushToast } from '@/lib/toast';

export default defineComponent({
  name: 'Customers',
  components: {
    CustomerDetails,
    CreateCustomerModal,
    PaginationBar,
    SearchInput,
    ListPage,
    Modal,
  },
  data() {
    return {
      customers: [],
      customersCount: null,
      searchTerm: '',
      page: 0,
      showActiveOnly: true,
      showDeactivatedOnly: false,
      currentCustomerId: -1,
      showingCustomerType: 'billing',
      isCreateCustomerOpen: false,
      isCustomerDetailsOpen: false,
      user: null,
      isStatusModalOpen: false,
      customerToUpdate: null as any,
      isProcessing: false,
    };
  },
  mounted() {
    const id = this.$route?.params?.id;
    if (id) {
      this.currentCustomerId = Number(id);
      this.isCustomerDetailsOpen = true;
    }
  },
  watch: {
    '$route.params.id': {
    handler(val) {
      if (val) {
        this.currentCustomerId = Number(val);
        this.isCustomerDetailsOpen = true;
      } else {
        this.isCustomerDetailsOpen = false;
        this.currentCustomerId = -1;
      }
    },
    immediate: true
  }
  },
  computed: {
    totalPages(): number {
      const pageSize = 20;
      const total = Number(this.customersCount || 0);
      return Math.max(1, Math.ceil(total / pageSize));
    },
    isAdmin(): boolean {
      return (this as any).user?.isAdmin || false;
    },
    sortedCustomers(): any[] {
      if (!this.customers) return [];
      return [...this.customers].sort((a, b) => a.name.localeCompare(b.name));
    },
    isSearching(): boolean {
      return this.searchTerm.length > 0;
    },
  },
  methods: {
    toggleActiveFilter() {
      if (this.showActiveOnly) this.showDeactivatedOnly = false;
      this.page = 0;
    },
    toggleDeactivatedFilter() {
      if (this.showDeactivatedOnly) this.showActiveOnly = false;
      this.page = 0;
    },
    openStatusModal(customer: any) {
      this.customerToUpdate = customer;
      this.isStatusModalOpen = true;
    },
    confirmToggleActive() {
      this.isProcessing = true;
      const newStatus = !this.customerToUpdate.isActive;

      (this as any).$apollo.mutate({
        mutation: EditCustomerActive,
        variables: {
          customerId: this.customerToUpdate.id,
          isActive: newStatus
        }
      }).then(() => {
        pushToast(`Customer ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        this.isStatusModalOpen = false;
        (this as any).$apollo.queries.customers.refetch();
      }).catch((err) => {
        console.error(err);
        pushToast('Failed to update customer status', 'error');
      }).finally(() => {
        this.isProcessing = false;
      });
    },
    closeCustomerDetails() {
      this.isCustomerDetailsOpen = false;
      this.currentCustomerId = -1;
      const path = this.$route?.path || '';
      if (path.includes('/customers') && path !== '/customers/') {
        this.$router.replace('/customers/');
      }
    },
    goToPage(idx: number) {
      const next = Math.max(0, Math.min(idx, this.totalPages - 1));
      if (next !== this.page) this.page = next;
    },
    customerType(customer: Customer): string {
      if (customer.billingAddress && customer.deliveryAddress) return 'Billing and Receiving';
      return customer.billingAddress ? 'Billing' : 'Receiving';
    },
    setCustomerType(type) {
      this.showingCustomerType = type;
      this.page = 0;
    },
    updatePage(inc: number) {
      if (inc > 0 && this.customers.length === 0) return;
      if (inc < 0 && this.page === 0) return;
      this.page += inc;
    },
    selectCustomer(id: any) {
      const desRoute = `/customers/${id}`;
      if (this.$route.path !== desRoute) {
        this.$router.push(desRoute);
      }
    },
    createdCustomer() {
      (this as any).$apollo.queries.customers.refetch();
      this.$emit('createdCustomer');
    },
  },
  apollo: {
    user: { query: UserQuery },
    customers: {
      query: Customers,
      variables() {
        let activeParam = undefined;
        if (this.showActiveOnly && !this.showDeactivatedOnly) activeParam = true;
        else if (!this.showActiveOnly && this.showDeactivatedOnly) activeParam = false;

        return {
          page: this.page,
          showActiveOnly: activeParam,
          name: this.searchTerm === '' ? undefined : this.searchTerm,
          customerType: this.searchTerm === '' ? this.showingCustomerType : undefined,
        };
      },
      debounce: 350,
      result({ data }) {
        this.customers = data.customers;
        this.customersCount = data.customersCount;
      },
    },
  },
});
</script>